import type { ReactNode } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useCustomerAuth } from "@/lib/auth/customer-auth-context";

/**
 * Garde d'accès à l'espace client (`/compte/**`, PAS `/connexion`) — pendant
 * client de `@/components/admin/AdminRouteGuard.tsx` (même doc de tête à lire
 * pour le détail : même principe, mêmes limites).
 *
 * apps/web n'a aujourd'hui AUCUNE garde équivalente au niveau layout — chaque
 * page `(account)/compte/*` vérifie elle-même sa session via
 * `getAuthSession()` côté serveur et redirige individuellement. Ce SPA n'a
 * plus ce runtime serveur : la garde ne peut être qu'un composant React monté
 * au clic/à la navigation, qui redirige vers `/connexion` si
 * `useCustomerAuth().isLoggedIn` est faux — centralisée dans
 * `@/layouts/AccountLayout.tsx` (cf. son propre commentaire) plutôt que
 * répétée dans chaque page `/compte/**`.
 *
 * IMPORTANT — ceci est de l'UX, PAS de la sécurité : `isLoggedIn` ne reflète
 * qu'un indice local non fiable (cf. src/lib/auth/customer-auth-context.tsx,
 * localStorage, jamais le cookie httpOnly réel). N'importe qui peut désactiver
 * JS ou appeler l'API directement ; la seule vraie protection est, comme
 * aujourd'hui, côté serveur (les guards `apps/api`, inchangés par ce lot). Ce
 * composant évite seulement d'afficher un flash d'UI compte vide à un
 * visiteur non connecté.
 */
export function AccountRouteGuard({ children }: { children: ReactNode }) {
  const { isLoggedIn } = useCustomerAuth();
  const location = useLocation();

  if (!isLoggedIn) {
    return <Navigate to="/connexion" replace state={{ from: location.pathname }} />;
  }

  return <>{children}</>;
}
